import {
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { InvoiceRentRow } from "services/api/fleet/vehicles/invoices/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Badge from "ui/components/Badge/Badge";
import { getStatusBadgeVariant } from "services/api/offers/consts";
import Tooltip from "ui/components/Tooltip/Tooltip";
import ContactCell from "ui/components/Table/TableSearch/components/Cells/ContactCell";
import ContractCell from "ui/components/Table/TableSearch/components/Cells/ContractCell";
import OfferCell from "ui/components/Table/TableSearch/components/Cells/OfferCell";
import { PictureIcon } from "ui/components/Pictures";

export const FLEET_VEHICLE_INVOICE_RENT_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleContactClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void
): Column[] => [
    {
      title: "",
      key: "invoice_pdf",
      field: "",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        if (!row.pdfUpload) return null;
        // Klik otvara generirani PDF računa u novom tabu.
        return (
          <PictureIcon
            url={row.pdfUpload.url}
            fileName={row.pdfUpload.originalFileName}
            tooltip={row.pdfUpload.originalFileName}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.invoicenumber",
      key: "invoice_number",
      field: "invoiceNumber",
      type: ColumnTypes.string,
    },
    {
      title: "admin.fleet.vehicle.invoice.search.contract",
      key: "invoice_contract",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return <ContractCell contract={row.contract} />;
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.offer",
      key: "invoice_offer",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return <OfferCell offer={row.offer} />;
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.client",
      key: "invoice_client",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return (
          <ContactCell
            contact={row.contactClient}
            onClick={(e) => row.contactClient?.id && handleContactClick(e, row.contactClient.id)}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.status",
      key: "invoice_status",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        if (!row.status) return null;
        return (
          <Badge
            text={row.status.name}
            variant={getStatusBadgeVariant(row.status.color)}
            height="small"
            showDot={true}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.invoicedate",
      key: "invoice_date",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return <div>{transformDateToLocalDateString(row.invoiceDate)}</div>;
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.duedate",
      key: "invoice_duedate",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        const dateStr = transformDateToLocalDateString(row.dueDate);
        if (!row.durationDays) return <>{dateStr}</>;
        return (
          <Tooltip decorate={<span>{dateStr}</span>} position="bottom-left" size="medium">
            <div className="min-w-[200px]">
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji dospijeća</div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Datum dospijeća</span>
                <span className="font-medium">{dateStr}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Broj dana</span>
                <span className="font-bold text-sky-400">{row.durationDays}</span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "admin.fleet.vehicle.invoice.search.total",
      key: "invoice_total",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => (
        <Tooltip
          decorate={<span className="text-md font-medium">{formatCurrency.format(row.total ?? 0)}</span>}
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[220px]">
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled računa</div>
            {row.buyerType && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Tip kupca</span>
                <span className="font-medium">{row.buyerType.name}</span>
              </div>
            )}
            {row.taxRate != null && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">PDV</span>
                <span className="font-medium">{row.taxRate}%</span>
              </div>
            )}
            <div className="border-t border-slate-600 my-2"></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Ukupno</span>
              <span className="font-bold text-green-400">{formatCurrency.format(row.total ?? 0)}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Kreirao",
      key: "invoice_created_by",
      defaultVisible: false,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return (
          <div className="text-sm text-slate-500">{row.userCreatedName ?? "-"}</div>
        );
      },
    },
    {
      title: "",
      key: "invoice_actions",
      alwaysVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: InvoiceRentRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];
